import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/api";
import i18n from "@/i18n";

// v2.0.11 — Contexte multidevise · Phase 81 — langues & devises administrables (chargées depuis /api/i18n/config)
// - Les montants sont stockés en EUR côté backend (source de vérité)
// - `convert(amount, from='EUR')` + `format(amount, from='EUR')` + `formatEur(amount)` (rappel en euros)
// - `currency` est persistée dans localStorage ; `languages` alimente le sélecteur de langue

const CurrencyContext = createContext(null);
const LS_KEY = "pag_currency";
const DEFAULT_CURRENCY = "EUR";

const DEFAULT_LANGUAGES = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "sv", label: "Svenska", flag: "🇸🇪" },
];
const DEFAULT_CURRENCIES = [
  { code: "EUR", symbol: "€", locale: "fr-FR", label: "Euro" },
  { code: "USD", symbol: "$", locale: "en-US", label: "US Dollar" },
  { code: "GBP", symbol: "£", locale: "en-GB", label: "British Pound" },
  { code: "SEK", symbol: "kr", locale: "sv-SE", label: "Swedish Krona" },
];
const DEFAULT_RATES = { EUR: 1, USD: 1.15, GBP: 0.85, SEK: 11.3 };

// Une langue ajoutée par l'admin n'a pas de fichier de ressources : on déclare un bundle minimal pour
// qu'i18next la « résolve » (sinon resolvedLanguage retombe sur fr et l'auto-traduction IA ne démarre pas).
function syncI18nLanguages(langs) {
  for (const l of langs) {
    if (!i18n.hasResourceBundle(l.code, "translation")) i18n.addResourceBundle(l.code, "translation", { __lang: l.code }, true, true);
  }
  const current = (i18n.language || "fr").split("-")[0];
  if (!langs.some((l) => l.code === current)) { i18n.changeLanguage("fr"); return; }
  if ((i18n.resolvedLanguage || "").split("-")[0] !== current) i18n.changeLanguage(current);
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || DEFAULT_CURRENCY; }
    catch { return DEFAULT_CURRENCY; }
  });
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [source, setSource] = useState("fallback");
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/i18n/config");
      const curs = data.currencies?.length ? data.currencies : DEFAULT_CURRENCIES;
      const langs = data.languages?.length ? data.languages : DEFAULT_LANGUAGES;
      setRates(data.rates || {});
      setUpdatedAt(data.updated_at);
      setSource(data.source);
      setCurrencies(curs);
      setLanguages(langs);
      syncI18nLanguages(langs);
      setCurrencyState((c) => (curs.some((x) => x.code === c) ? c : DEFAULT_CURRENCY));
    } catch (e) { /* garde les valeurs par défaut */ }
    finally { setReady(true); }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { try { localStorage.setItem(LS_KEY, currency); } catch { /* ignore */ } }, [currency]);

  const setCurrency = useCallback((code) => {
    if (!currencies.some((c) => c.code === code)) return;
    setCurrencyState(code);
  }, [currencies]);

  const meta = useMemo(() => currencies.find((c) => c.code === currency) || { code: currency, symbol: currency, locale: "fr-FR" }, [currencies, currency]);

  // Convertit un montant `amount` exprimé dans `from` vers la `currency` courante.
  const convert = useCallback((amount, from = "EUR") => {
    if (amount == null || isNaN(Number(amount))) return null;
    const a = Number(amount);
    if (from === currency) return a;
    const rFrom = rates[from] || 1;
    const rTo = rates[currency] || 1;
    return (a / rFrom) * rTo;
  }, [currency, rates]);

  const format = useCallback((amount, from = "EUR", opts = {}) => {
    const v = convert(amount, from);
    if (v == null) return "";
    const minFrac = opts.minFrac ?? (Math.abs(v) < 10 ? 2 : 0);
    const maxFrac = opts.maxFrac ?? (Math.abs(v) < 10 ? 2 : 0);
    try {
      return new Intl.NumberFormat(meta.locale || "fr-FR", { style: "currency", currency, minimumFractionDigits: minFrac, maximumFractionDigits: maxFrac }).format(v);
    } catch {
      return `${Math.round(v)} ${meta.symbol || currency}`;
    }
  }, [currency, convert, meta]);

  const formatEur = useCallback((amount, opts = {}) => {
    if (amount == null || isNaN(Number(amount))) return "";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: opts.minFrac ?? 0, maximumFractionDigits: opts.maxFrac ?? 0 }).format(Number(amount));
  }, []);

  const value = useMemo(() => ({
    currency, setCurrency, isEur: currency === "EUR",
    rates, currencies, languages, updatedAt, source, ready, reload,
    convert, format, formatEur,
    symbol: meta.symbol || currency,
  }), [currency, setCurrency, rates, currencies, languages, updatedAt, source, ready, reload, convert, format, formatEur, meta]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
